/**
 * 앱 버전 비교와 '새 버전 안내' 판정 (2026-10-08, 1.0.1 부터).
 * 서버 설정 settings.appUpdate(schemas/settings.ts)의 최소·권장 버전과 앱의 스토어 버전(expo-application
 * nativeApplicationVersion)을 비교한다. 앱(src/lib/app-update.ts)과 관리자 화면 검증, API 테스트가 같은 함수를 쓴다.
 */

/**
 * 앱이 모든 API 요청에 싣는 자기 버전 헤더 (2026-10-16, 1.0.3 부터. apps/mobile/src/lib/client-headers.ts).
 * 서버는 아직 읽지 않는다. 읽게 되면 '지원 종료 버전' 응답(설계: docs/운영-개발-안내서.md 6.9 끝)을 줄 수 있다.
 * 1.0.0 과 코덱스 빌드(iOS 1.0.2 · Android 1.0.1)는 이 헤더를 보내지 않는다 — 헤더가 없으면 '1.0.3 이전 앱'이다.
 */
/** 스토어 표기 버전 (expo-application nativeApplicationVersion, 예: 1.0.3). OTA 와 무관한 바이너리 값 */
export const APP_VERSION_HEADER = 'X-App-Version'
/** 빌드 번호 (iOS buildNumber · Android versionCode, expo-application nativeBuildVersion) */
export const APP_BUILD_HEADER = 'X-App-Build'
/** 'ios' | 'android' — settings.appUpdate 가 플랫폼별이라 서버가 판정할 때 필요하다 */
export const APP_PLATFORM_HEADER = 'X-App-Platform'

/** 스토어 페이지 기본 주소. 설정의 storeUrl 이 비었을 때 쓴다 (별점 요청 등 다른 곳도 이 상수를 쓴다) */
export const DEFAULT_STORE_URLS = {
  /** eas.json submit.production.ios.ascAppId */
  ios: 'https://apps.apple.com/app/id6815311551',
  /** app.config.ts android.package (운영) */
  android: 'https://play.google.com/store/apps/details?id=app.getrecipecat',
} as const

export type AppUpdatePlatform = keyof typeof DEFAULT_STORE_URLS

/** '1.2.3' 형식만 받는다. 앞뒤 공백·'v' 접두·두 자리 형식('1.0')은 null */
export function parseAppVersion(v: string): [number, number, number] | null {
  const m = /^(\d{1,4})\.(\d{1,4})\.(\d{1,4})$/.exec(v)
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null
}

/** 숫자 단위 비교 (1.0.10 > 1.0.9). 형식이 틀리면 null */
export function compareAppVersions(a: string, b: string): -1 | 0 | 1 | null {
  const x = parseAppVersion(a)
  const y = parseAppVersion(b)
  if (!x || !y) return null
  const d = x[0] - y[0] || x[1] - y[1] || x[2] - y[2]
  return d === 0 ? 0 : d < 0 ? -1 : 1
}

export type AppUpdateStatus = 'required' | 'recommended' | 'none'

/**
 * 현재 버전이 최소 버전 미만이면 'required'(닫을 수 없는 업데이트 화면), 권장 버전 미만이면 'recommended'(닫을 수 있는 안내).
 * 현재 버전을 못 읽었거나, 설정이 비었거나(''= 안내 없음), 형식이 틀리면 'none' 이다.
 * 오프라인·설정 오류로 사용자를 막지 않기 위해 판정이 애매하면 항상 안내하지 않는 쪽으로 간다.
 */
export function appUpdateStatus(
  current: string | null | undefined,
  target: { minVersion?: string; latestVersion?: string } | null | undefined,
): AppUpdateStatus {
  if (!current || !target) return 'none'
  if (target.minVersion && compareAppVersions(current, target.minVersion) === -1) return 'required'
  if (target.latestVersion && compareAppVersions(current, target.latestVersion) === -1) {
    return 'recommended'
  }
  return 'none'
}
